import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from '../AdminTracks.module.css';
import {
  createAdminCategory,
  deleteAdminCategory,
  updateAdminCategory,
} from '../../utils/api';
import {
  FeedbackBanner,
  categoryId,
  categoryName,
  getCategoryCoverUrl,
} from './helpers';

const IMAGE_FILE_ERROR = 'Please select an image file only.';

function useCoverPreview() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleChange = useCallback((event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) {
      setFile(null);
      setPreviewUrl(null);
      return null;
    }
    if (!nextFile.type.startsWith('image/')) {
      event.target.value = '';
      setFile(null);
      setPreviewUrl(null);
      return IMAGE_FILE_ERROR;
    }
    setFile(nextFile);
    setPreviewUrl(URL.createObjectURL(nextFile));
    return null;
  }, []);

  return { file, previewUrl, handleChange };
}

function portalToBody(node) {
  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}

function Field({ id, label, required, optional, children }) {
  return (
    <div className={styles['field']}>
      <label className={styles['label']} htmlFor={id}>
        {label}{' '}
        {required ? <span className={styles['required']}>*</span> : null}
        {optional ? <span className={styles['optional']}>(optional)</span> : null}
      </label>
      {children}
    </div>
  );
}

function CoverFileInput({ id, onChange }) {
  return (
    <div className={styles['file-row']}>
      <input
        id={id}
        type="file"
        accept="image/*"
        className={styles['file-input']}
        onChange={onChange}
      />
      <label htmlFor={id} className={styles['file-button']}>Choose Image</label>
    </div>
  );
}

function CoverPreview({ src, emptyLabel, alt }) {
  return src ? (
    <img className={styles['cover-preview-img']} src={src} alt={alt} />
  ) : (
    <div className={styles['cover-preview-empty']}>{emptyLabel}</div>
  );
}

function DialogActions({ onCancel, cancelLabel = 'Cancel', submitLabel, submitting, submitBusyLabel, danger, onSubmitClick }) {
  return (
    <div className={styles['dialog-actions']}>
      <button type="button" className={styles['secondary']} onClick={onCancel} disabled={submitting}>
        {cancelLabel}
      </button>
      {onSubmitClick ? (
        <button
          type="button"
          className={danger ? `${styles['primary']} ${styles['primary-danger']}` : styles['primary']}
          onClick={onSubmitClick}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? submitBusyLabel : submitLabel}
        </button>
      ) : (
        <button type="submit" className={styles['primary']} disabled={submitting} aria-busy={submitting}>
          {submitting ? submitBusyLabel : submitLabel}
        </button>
      )}
    </div>
  );
}

export function CreateCategoryModal({ navigate, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const cover = useCoverPreview();

  const handleCoverChange = (event) => {
    const error = cover.handleChange(event);
    setFeedback(error ? { type: 'error', message: error } : null);
  };

  const submit = async (event) => {
    event.preventDefault();
    const nextName = name.trim();
    if (!nextName) return setFeedback({ type: 'error', message: 'Name is required.' });
    if (!cover.file) return setFeedback({ type: 'error', message: 'Cover image is required.' });

    setSubmitting(true);
    setFeedback(null);
    const result = await createAdminCategory(
      { coverImageFile: cover.file, name: nextName },
      navigate,
    );
    setSubmitting(false);
    if (!result.ok) {
      setFeedback({ type: 'error', message: result.message || 'Create failed.' });
      return;
    }
    onCreated(result.message);
  };

  return (
    <div className={styles['sheet-overlay']} role="presentation">
      <div className={styles['sheet']} role="dialog" aria-modal="true" aria-labelledby="admin-category-create-title">
        <div className={styles['sheet-header']}>
          <h2 id="admin-category-create-title" className={styles['sheet-title']}>Create Category</h2>
          <button type="button" className={styles['dialog-close']} onClick={onClose} aria-label="Close create dialog">×</button>
        </div>
        <form className={styles['sheet-body']} onSubmit={submit}>
          <Field id="admin-category-create-name-input" label="Name" required>
            <input
              id="admin-category-create-name-input"
              className={styles['text-input']}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="off"
              maxLength={64}
            />
          </Field>
          <Field id="admin-category-create-cover-input" label="Cover Image" required>
            <CoverFileInput id="admin-category-create-cover-input" onChange={handleCoverChange} />
            <div className={styles['single-cover-preview']}>
              <CoverPreview
                src={cover.previewUrl && cover.file ? cover.previewUrl : null}
                emptyLabel="Select an image to preview"
                alt="Cover preview"
              />
            </div>
          </Field>
          <FeedbackBanner feedback={feedback} />
          <DialogActions
            onCancel={onClose}
            submitLabel="Create"
            submitBusyLabel="Creating…"
            submitting={submitting}
          />
        </form>
      </div>
    </div>
  );
}

export function EditCategoryModal({ category, navigate, onClose, onSaved }) {
  const [name, setName] = useState(categoryName(category));
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const cover = useCoverPreview();
  const existingCoverUrl = getCategoryCoverUrl(category);
  const newCoverUrl = cover.previewUrl && cover.file ? cover.previewUrl : null;

  const handleCoverChange = (event) => {
    const error = cover.handleChange(event);
    setFeedback(error ? { type: 'error', message: error } : null);
  };

  const submit = async (event) => {
    event.preventDefault();
    const id = categoryId(category);
    const nextName = name.trim();
    if (!id) return;
    if (!nextName) return setFeedback({ type: 'error', message: 'Name is required.' });

    setSubmitting(true);
    setFeedback(null);
    const result = await updateAdminCategory(
      { id, coverImageFile: cover.file, name: nextName },
      navigate,
    );
    setSubmitting(false);
    if (!result.ok) {
      setFeedback({ type: 'error', message: result.message || 'Update failed.' });
      return;
    }
    onSaved(result.message);
  };

  return portalToBody(
    <div className={styles['dialog-overlay']} role="presentation">
      <div className={styles['dialog']} role="dialog" aria-modal="true" aria-labelledby="admin-category-edit-title">
        <div className={styles['dialog-header']}>
          <h2 id="admin-category-edit-title" className={styles['dialog-title']}>Edit Category</h2>
          <button type="button" className={styles['dialog-close']} onClick={onClose} aria-label="Close edit dialog">×</button>
        </div>
        <form className={styles['dialog-body']} onSubmit={submit}>
          <Field id="admin-category-edit-name-input" label="Name" required>
            <input
              id="admin-category-edit-name-input"
              className={styles['text-input']}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="off"
              maxLength={64}
            />
          </Field>
          <Field id="admin-category-edit-cover-input" label="Replace Cover Image" optional>
            <CoverFileInput id="admin-category-edit-cover-input" onChange={handleCoverChange} />
            <div className={styles['cover-preview-grid']}>
              <div className={styles['cover-preview-col']}>
                <p className={styles['cover-preview-title']}>Current</p>
                <div className={styles['cover-preview-box']}>
                  <CoverPreview src={existingCoverUrl} emptyLabel="No cover" alt="Current cover" />
                </div>
              </div>
              <div className={styles['cover-preview-col']}>
                <p className={styles['cover-preview-title']}>New</p>
                <div className={styles['cover-preview-box']}>
                  <CoverPreview src={newCoverUrl} emptyLabel="Select an image" alt="New cover preview" />
                </div>
              </div>
            </div>
          </Field>
          <FeedbackBanner feedback={feedback} />
          <DialogActions
            onCancel={onClose}
            submitLabel="Save"
            submitBusyLabel="Saving…"
            submitting={submitting}
          />
        </form>
      </div>
    </div>
  );
}

export function DeleteCategoryModal({ category, navigate, onClose, onDeleted }) {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const name = categoryName(category) || 'Untitled';
  const trackCount = Number(category?.track_count) || 0;

  const confirmDelete = async () => {
    const id = categoryId(category);
    if (!id) return;
    setSubmitting(true);
    setFeedback(null);
    const result = await deleteAdminCategory({ id }, navigate);
    setSubmitting(false);
    if (!result.ok) {
      setFeedback({ type: 'error', message: result.message || 'Delete failed.' });
      return;
    }
    onDeleted(result.message);
  };

  return portalToBody(
    <div className={styles['dialog-overlay']} role="presentation">
      <div className={`${styles['dialog']} ${styles['dialog-danger']}`} role="dialog" aria-modal="true" aria-labelledby="admin-category-delete-title">
        <div className={styles['dialog-header']}>
          <h2 id="admin-category-delete-title" className={styles['dialog-title']}>Delete Category</h2>
          <button type="button" className={styles['dialog-close']} onClick={onClose} aria-label="Close delete dialog">×</button>
        </div>
        <div className={styles['dialog-body']}>
          <p className={styles['delete-text']}>
            Are you sure you want to delete <strong>{name}</strong>?
            {trackCount > 0
              ? ` This category still has ${trackCount} ${trackCount === 1 ? 'track' : 'tracks'}. Move or delete those tracks first.`
              : ' This hides it from the catalog (soft delete).'}
          </p>
          <FeedbackBanner feedback={feedback} />
          <DialogActions
            onCancel={onClose}
            submitLabel="Delete"
            submitBusyLabel="Deleting…"
            submitting={submitting}
            danger
            onSubmitClick={confirmDelete}
          />
        </div>
      </div>
    </div>
  );
}
